<template>
  <div class="container mx-auto px-4 py-8">
    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-white mb-4">Product FAQs</h1>
          <p class="text-gray-400">View and manage questions asked about your products</p>
        </div>
        <div class="flex gap-3">
          <button
            @click="showTestModal = true"
            class="inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20 transition-colors"
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            Test FAQ
          </button>
          <button
            @click="fetchFAQs"
            class="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-dark px-4 py-2 text-sm font-medium text-gray-300 hover:border-primary/30 hover:bg-primary/10 hover:text-primary transition-colors"
            :disabled="loading"
          >
            <svg 
              class="h-4 w-4"
              :class="{ 'animate-spin': loading }"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {{ loading ? 'Refreshing...' : 'Refresh' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-dark-secondary rounded-lg p-6 mb-8">
      <div class="relative">
        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          v-model="searchTerm"
          type="search"
          class="block w-full rounded-lg border border-gray-700 bg-dark pl-10 pr-3 py-2 text-sm text-gray-100 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="Search questions or products..."
        />
      </div>
    </div>

    <!-- Results -->
    <div class="overflow-x-auto">
      <div class="bg-dark-secondary rounded-xl shadow-lg">
        <table class="min-w-full">
          <thead class="border-b border-gray-700">
            <tr>
              <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">
                Time
              </th>
              <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">
                Product
              </th>
              <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">
                Type
              </th>
              <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">
                Question
              </th>
              <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">
                Answer
              </th>
              <th scope="col" class="px-4 py-4 text-right text-xs font-medium text-gray-300 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-700/50">
            <tr v-if="loading && !faqs.length" v-for="i in 5" :key="`skeleton-${i}`">
              <td class="px-6 py-4 whitespace-nowrap"><div class="h-4 bg-gray-700/50 rounded animate-pulse w-24"></div></td>
              <td class="px-6 py-4"><div class="h-4 bg-gray-700/50 rounded animate-pulse w-32"></div></td>
              <td class="px-6 py-4"><div class="h-4 bg-gray-700/50 rounded animate-pulse w-48"></div></td>
              <td class="px-6 py-4"><div class="h-4 bg-gray-700/50 rounded animate-pulse w-64"></div></td>
              <td class="px-4 py-4 text-right"><div class="h-8 w-8 bg-gray-700/50 rounded-lg animate-pulse ml-auto"></div></td>
            </tr>
            <tr v-else-if="!filteredFAQs.length" class="hover:bg-gray-700/20 transition-colors">
              <td colspan="5" class="px-6 py-8 text-sm text-gray-500 text-center">
                No FAQs found
              </td>
            </tr>
            <tr v-else v-for="faq in filteredFAQs" :key="faq.id" class="hover:bg-gray-700/20 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                {{ formatDate(faq.created_at) }}
              </td>
              <td class="px-6 py-4 text-sm text-gray-300">
                <div class="font-medium text-white">{{ faq.product_title }}</div>
                <div class="text-xs text-gray-500">{{ faq.product_handle }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getQueryTypeClass(faq.query_type)"
                >
                  {{ formatQueryType(faq.query_type) }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm text-gray-300">
                <div class="max-w-xs truncate" :title="faq.query">{{ faq.query }}</div>
              </td>
              <td class="px-6 py-4 text-sm text-gray-300">
                <div class="max-w-md line-clamp-2" :title="faq.answer">{{ faq.answer }}</div>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-right text-sm">
                <div class="flex items-center gap-2 justify-end">
                  <button 
                    @click="openDetailsModal(faq)"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-700 text-gray-300 hover:border-primary/30 hover:bg-primary/10 hover:text-primary transition-colors"
                    title="View Details"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <button
                    @click="handleDeleteFAQ(faq)"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-red-500/30 text-red-500 hover:border-red-500/50 hover:bg-red-500/10 transition-colors"
                    title="Delete FAQ"
                  >
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Details Modal -->
    <BaseModal v-model="showDetailsModal" max-width="50rem">
      <template #title>FAQ Details</template>
      <div v-if="selectedFAQ" class="space-y-6">
        <div class="flex justify-between items-start">
          <div>
            <h4 class="text-sm font-medium text-gray-400 mb-1">Product</h4>
            <p class="text-white font-medium">{{ selectedFAQ.product_title }} ({{ selectedFAQ.product_handle }})</p>
          </div>
          <span 
            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
            :class="getQueryTypeClass(selectedFAQ.query_type)"
          >
            {{ formatQueryType(selectedFAQ.query_type) }}
          </span>
        </div>
        <div>
          <h4 class="text-sm font-medium text-gray-400 mb-1">Question</h4>
          <p class="text-white text-lg">{{ selectedFAQ.query }}</p>
        </div>
        <div>
          <h4 class="text-sm font-medium text-gray-400 mb-1">Answer</h4>
          <div class="bg-dark p-4 rounded-lg border border-gray-700">
            <p class="text-gray-200 whitespace-pre-wrap">{{ selectedFAQ.answer }}</p>
          </div>
        </div>
        <div class="flex justify-between text-xs text-gray-500 pt-4 border-t border-gray-700">
          <span>ID: {{ selectedFAQ.id }}</span>
          <span>Asked on: {{ formatDate(selectedFAQ.created_at) }}</span>
        </div>
      </div>
      <template #footer>
        <button @click="showDetailsModal = false" class="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white">Close</button>
      </template>
    </BaseModal>

    <!-- Test FAQ Modal -->
    <BaseModal v-model="showTestModal" max-width="40rem">
      <template #title>Test FAQ Generation</template>
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-1">Select Product</label>
          <select 
            v-model="testForm.product_handle"
            class="w-full bg-dark border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
          >
            <option value="">Select a product...</option>
            <option v-for="product in productStore.products" :key="product.id" :value="product.handle">
              {{ product.title }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-1">Question</label>
          <textarea 
            v-model="testForm.query"
            rows="3"
            class="w-full bg-dark border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
            placeholder="e.g. Is this product waterproof?"
          ></textarea>
        </div>
        
        <div v-if="testResult" class="mt-6 space-y-4">
          <div class="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <h4 class="text-sm font-medium text-primary mb-2">Generated Answer:</h4>
            <p class="text-gray-200">{{ testResult.answer }}</p>
          </div>
        </div>

        <div v-if="testError" class="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
          <p class="text-sm text-red-400">{{ testError }}</p>
        </div>
      </div>
      <template #footer>
        <div class="flex gap-3">
          <button 
            @click="showTestModal = false" 
            class="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white"
          >Cancel</button>
          <button 
            @click="handleTestFAQ"
            :disabled="isTesting || !testForm.product_handle || !testForm.query"
            class="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            <svg v-if="isTesting" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isTesting ? 'Generating...' : 'Generate Answer' }}
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/config/firebase'
import { useOrgStore } from '@/stores/org'
import { useProductStore } from '@/stores/products'
import BaseModal from '@/components/ui/BaseModal.vue'
import { Timestamp } from 'firebase/firestore'

const orgStore = useOrgStore()
const productStore = useProductStore()

const faqs = ref<any[]>([])
const loading = ref(true)
const searchTerm = ref('')

const showDetailsModal = ref(false)
const selectedFAQ = ref<any>(null)

const showTestModal = ref(false)
const isTesting = ref(false)
const testError = ref('')
const testResult = ref<any>(null)
const testForm = ref({
  product_handle: '',
  query: ''
})

const fetchFAQs = async () => {
  try {
    loading.value = true
    const getFAQsFn = httpsCallable(functions, 'getFAQs')
    const result = await getFAQsFn()
    const data = result.data as { success: boolean; faqs: any[] }
    if (data.success) {
      faqs.value = data.faqs
    }
  } catch (error) {
    console.error('Error fetching FAQs:', error)
  } finally {
    loading.value = false
  }
}

const filteredFAQs = computed(() => {
  if (!searchTerm.value) return faqs.value
  const term = searchTerm.value.toLowerCase()
  return faqs.value.filter(faq => 
    faq.query.toLowerCase().includes(term) || 
    faq.product_title.toLowerCase().includes(term) ||
    faq.product_handle.toLowerCase().includes(term) ||
    faq.answer.toLowerCase().includes(term)
  )
})

const handleTestFAQ = async () => {
  if (!testForm.value.product_handle || !testForm.value.query) return
  
  isTesting.value = true
  testError.value = ''
  testResult.value = null

  try {
    // We use fetch for the onRequest function
    const isDev = process.env.NODE_ENV === 'development'
    const baseUrl = isDev 
      ? 'http://127.0.0.1:5001/search-plus-3bf60/australia-southeast1/askFAQ' 
      : 'https://australia-southeast1-search-plus-3bf60.cloudfunctions.net/askFAQ'

    const queryParams = new URLSearchParams({
      query: testForm.value.query,
      org_id: orgStore.orgData?.id || '',
      product_handle: testForm.value.product_handle
    })

    const response = await fetch(`${baseUrl}?${queryParams}`)

    const data = await response.json()
    if (data.success) {
      testResult.value = data.data
      fetchFAQs() // Refresh list to show new FAQ
    } else {
      testError.value = data.message || 'Failed to generate answer'
    }
  } catch (error) {
    console.error('Error testing FAQ:', error)
    testError.value = 'An error occurred while generating the answer'
  } finally {
    isTesting.value = false
  }
}

const handleDeleteFAQ = async (faq: any) => {
  if (!confirm('Are you sure you want to delete this FAQ?')) return

  try {
    const deleteFAQFn = httpsCallable(functions, 'deleteFAQ')
    await deleteFAQFn({ faq_id: faq.id })
    faqs.value = faqs.value.filter(f => f.id !== faq.id)
    if (selectedFAQ.value?.id === faq.id) {
      showDetailsModal.value = false
    }
  } catch (error) {
    console.error('Error deleting FAQ:', error)
    alert('Failed to delete FAQ')
  }
}

const openDetailsModal = (faq: any) => {
  selectedFAQ.value = faq
  showDetailsModal.value = true
}

const formatDate = (date: any) => {
  if (!date) return ''
  const dateObj = date instanceof Timestamp ? date.toDate() : 
                  (date._seconds ? new Date(date._seconds * 1000) : new Date(date))
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(dateObj)
}

const formatQueryType = (type: string) => {
  if (!type) return 'Other'
  return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

const getQueryTypeClass = (type: string) => {
  switch (type) {
    case 'size_fit': return 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
    case 'colours': return 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
    case 'fabric': return 'bg-green-500/10 text-green-400 border border-green-500/20'
    case 'returns': return 'bg-red-500/10 text-red-400 border border-red-500/20'
    case 'activity_use_cases': return 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
    case 'price_discounting_packs': return 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
    case 'product_search': return 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
    default: return 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
  }
}

onMounted(async () => {
  await Promise.all([
    fetchFAQs(),
    productStore.fetchProducts()
  ])
})
</script>
